import * as yup from 'yup';
import { AnySchema } from 'yup';
import {
	WizardItem,
	WizardItemRule,
	WizardRootItem,
} from '../Interfaces/Wizard.interface';
import { WizardItemType } from '../Enums/WizardItemType.enum';
import useTranslation from '../../../Hooks/useTranslation';
import { WizardItemRuleType } from '../Enums/WizardItemRuleType.enum';
import moment, { Moment } from 'moment';
import {
	applyConditions,
	ConditionConfig,
	getConditionConfigFromItem,
} from '../Services/wizardCondition.service';
import { buildMultiplierSchema } from '../Services/wizardMultiplier.service';
import { meddiYup } from '../../BaseModule/Validation/meddiYup';

export default function useWizardForm() {
	const { t } = useTranslation();

	function isRequired(item: any): boolean {
		return Boolean(item?.settings?.required);
	}

	function isNullable(item: any): boolean {
		return Boolean(item?.settings?.nullable);
	}

	const parseToMoment = (value: unknown): Moment | null => {
		if (!value) return null;

		if (moment.isMoment(value)) {
			return value.isValid() ? value : null;
		}

		if (value instanceof Date) {
			const m = moment(value);
			return m.isValid() ? m : null;
		}

		if (typeof value === 'string') {
			const m = moment(value);
			return m.isValid() ? m : null;
		}

		return null;
	};

	const addRules = <T extends AnySchema>(
		yupItem: T,
		rules: WizardItemRule[],
	): T => {
		let schema: any = yupItem;

		for (const rule of rules || []) {
			switch (rule.type) {
				case WizardItemRuleType.MAX_LENGTH:
				case WizardItemRuleType.MAX:
					schema = schema.max(rule.value, rule.message);
					break;

				case WizardItemRuleType.MIN_LENGTH:
				case WizardItemRuleType.MIN:
					schema = schema.min(rule.value, rule.message);
					break;

				case WizardItemRuleType.EMAIL:
					if (typeof schema.email === 'function') {
						schema = schema.email(rule.message);
					}
					break;

				case WizardItemRuleType.AGE_MIN:
					schema = schema.test('age-min', rule.message, (value: unknown) => {
						if (!value) return true;

						const birthMoment = parseToMoment(value);
						if (!birthMoment) return false;

						const age = moment().diff(birthMoment, 'years');
						return age >= Number(rule.value);
					});
					break;

				case WizardItemRuleType.AGE_MAX:
					schema = schema.test('age-max', rule.message, (value: unknown) => {
						if (!value) return true;

						const birthMoment = parseToMoment(value);
						if (!birthMoment) return false;

						const age = moment().diff(birthMoment, 'years');
						return age < Number(rule.value);
					});
					break;

				case WizardItemRuleType.MIN_DATE:
					schema = schema.test('min-date', rule.message, (value: unknown) => {
						if (!value) return true;

						const inputMoment = parseToMoment(value);
						const minMoment = parseToMoment(rule.value);

						if (!inputMoment || !minMoment) return false;

						return inputMoment.isSameOrAfter(minMoment);
					});
					break;

				case WizardItemRuleType.MAX_DATE:
					schema = schema.test('max-date', rule.message, (value: unknown) => {
						if (!value) return true;

						const inputMoment = parseToMoment(value);
						const maxMoment = parseToMoment(rule.value);

						if (!inputMoment || !maxMoment) return false;

						return inputMoment.isSameOrBefore(maxMoment);
					});
					break;
			}
		}

		return schema as T;
	};

	function buildFieldSchema(item: WizardItem): yup.AnySchema | null {
		const required = isRequired(item);
		const nullable = isNullable(item);
		const errorMessage = `${t('validation.form.field')} ${t(item.title)} ${t(
			'validation.form.isRequiredToFill',
		)}`;

		let field: yup.AnySchema | null = null;

		switch (item.type as WizardItemType) {
			case WizardItemType.TEXT_FIELD:
			case WizardItemType.TEXT_FIELD_2:
			case WizardItemType.TEXT_AREA:
			case WizardItemType.TEXT_AREA_2:
				field = yup
					.string()
					.nullable()
					.transform(v => (v == null ? '' : v.trim?.() ?? v));
				break;
			case WizardItemType.CHECKBOX:
				field = yup.boolean();
				break;
			case WizardItemType.SELECT:
				field = meddiYup.select();
				break;
			case WizardItemType.DATE:
			case WizardItemType.DATE_PICKER:
				field = yup.mixed<moment.Moment | null>();
				break;
			case WizardItemType.USER_RELATIONS:
				field = yup.object().shape({
					workplaceId: meddiYup.select(),
					doctorId: meddiYup.select(),
				});
				break;
			case WizardItemType.UPLOAD:
				field = yup.mixed<File | null>();
				break;
			case WizardItemType.MULTI_UPLOAD: {
				field = yup.array().of(yup.mixed<File>());
				break;
			}
		}

		if (!field) return null;

		let schema: yup.AnySchema = field;

		if (item.settings?.rules) {
			schema = addRules(schema, item.settings.rules);
		}

		if (nullable) {
			schema = schema.nullable();
		}

		if (!required) return schema.nullable();

		if (item.type === WizardItemType.CHECKBOX) {
			return (schema as yup.BooleanSchema)
				.defined(errorMessage)
				.oneOf([true], errorMessage);
		}

		if (item.type === WizardItemType.SELECT) {
			return meddiYup.selectRequired(errorMessage);
		}

		if (item.type === WizardItemType.USER_RELATIONS) {
			return yup.object().shape({
				workplaceId: meddiYup.selectRequired(
					'validation.userRelations.workplaceId',
				),
				doctorId: meddiYup.selectRequired('validation.userRelations.doctorId'),
			});
		}

		return schema.required(errorMessage);
	}

	function collectShapes(
		items: WizardItem[],
		shape: Record<string, yup.AnySchema>,
		conditions: ConditionConfig[] = [],
	): void {
		for (const item of items ?? []) {
			if (item.type === WizardItemType.CONDITION) {
				const cfg = getConditionConfigFromItem(item);
				const next = cfg ? [...conditions, cfg] : conditions;
				if (Array.isArray(item?.children) && item.children.length > 0) {
					collectShapes(item.children, shape, next);
				}
				continue;
			}

			if (item.type === WizardItemType.AGREEMENT_LIST) {
				for (const child of item.children ?? []) {
					if (!child.key) continue;
					const required = Boolean(child.settings?.required);
					let checkboxSchema = yup.boolean();
					if (required) {
						const errorMessage = t('validation.wizard.agreementItem');
						checkboxSchema = checkboxSchema
							.defined(errorMessage)
							.oneOf([true], errorMessage);
					}
					shape[child.key] = applyConditions(checkboxSchema, conditions);
				}
				continue;
			}

			if (item.type === WizardItemType.MULTIPLIER) {
				const innerShape: Record<string, yup.AnySchema> = {};
				collectShapes(item.children ?? [], innerShape, conditions);
				const multiplierSchema = buildMultiplierSchema({
					item,
					itemSchemaShape: innerShape,
					t,
				});
				if (multiplierSchema && item.key) {
					shape[item.key] = applyConditions(multiplierSchema, conditions);
				}

				continue;
			}

			if (item?.key) {
				const fieldSchema = buildFieldSchema(item);
				if (fieldSchema) {
					shape[item.key] = applyConditions(fieldSchema, conditions);
				}
			}

			if (Array.isArray(item?.children) && item.children.length > 0) {
				collectShapes(item.children, shape, conditions);
			}
		}
	}

	const generateWizardFormValidationScheme = (
		activeStep: WizardRootItem | null,
	) => {
		const shape: Record<string, yup.AnySchema> = {};

		collectShapes(activeStep?.children ?? [], shape, []);

		return yup.object().shape(shape);
	};

	return { generateWizardFormValidationScheme };
}
